"use client";

import {
  ADDRESS_QUERY,
  type AddressInput,
  type AddressType,
  execute,
  QUERY_KEYS,
} from "@frontend/roz-sdk-core";
import { Input } from "@frontend/roz-uikit/studio";
import { useQuery } from "@tanstack/react-query";
import {
  type ChangeEvent,
  type FocusEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { cn } from "./cn";

export interface AddressSelectProps {
  onChange: (value: AddressInput) => void;
  onFocus?: () => void;
  value: AddressInput | null;
  fieldName?: string;
  inputPlaceholder?: string;
  startTypingPlaceholder?: string;
}

const REGION_SEARCH_DEBOUNCE_MS = 500;

const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

const useClickAway = <T extends HTMLElement>(handler: () => void) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (
        ref.current &&
        event.target instanceof Node &&
        !ref.current.contains(event.target)
      ) {
        handler();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [handler]);

  return ref;
};

const useRegions = (searchTerm: string) => {
  const debounced = useDebounced(searchTerm, REGION_SEARCH_DEBOUNCE_MS);
  const [regions, setRegions] = useState<AddressType[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.address({ term: debounced }),
    queryFn: () => execute(ADDRESS_QUERY, { term: debounced, all_world: true }),
    enabled: !!debounced,
  });

  useEffect(() => {
    if (data?.suggestAddress && data.suggestAddress.length > 0) {
      setRegions(
        data.suggestAddress.filter((r): r is AddressType => r !== null)
      );
    } else {
      setRegions([]);
    }
  }, [data]);

  return { regions, isLoading };
};

export const AddressSelect = ({
  value,
  inputPlaceholder = "Введите адрес",
  startTypingPlaceholder = "Начните вводить адрес",
  onChange,
  onFocus,
  fieldName,
}: AddressSelectProps) => {
  const [inputValue, setInputValue] = useState("");
  const [opened, setOpened] = useState(false);

  const regionsListRef = useClickAway<HTMLDivElement>(() => setOpened(false));

  const { regions, isLoading } = useRegions(inputValue);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setOpened(true);
  };

  const fieldId = `field-id-${fieldName}`;

  const handleSelectRegion = (region: AddressInput) => {
    onChange(region);
    setOpened(false);
    setInputValue("");
  };

  const handleFocusBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (event.type === "focus") {
      onFocus?.();
      setOpened(true);
    }
    if (event.type === "blur") {
      setOpened(false);
    }
  };

  const renderRegionsList = () => {
    if (isLoading) {
      return (
        <div className="sdkui:p-4 sdkui:text-center sdkui:text-dark-60 sdkui:text-sm">
          Идет поиск...
        </div>
      );
    }

    if (!inputValue?.trim() && regions.length === 0) {
      return (
        <div className="sdkui:p-4 sdkui:text-center sdkui:text-dark-60 sdkui:text-sm">
          {startTypingPlaceholder}
        </div>
      );
    }

    return regions.length > 0 ? (
      regions.map((region) => (
        <button
          className="sdkui:flex sdkui:w-full sdkui:cursor-pointer sdkui:items-center sdkui:rounded-lg sdkui:px-4 sdkui:py-2 sdkui:text-left sdkui:text-dark-100 sdkui:text-sm sdkui:transition-colors sdkui:hover:bg-zinc-50"
          key={region.fiasId}
          onClick={() => handleSelectRegion(region)}
          onMouseDown={(event) => event.preventDefault()}
          type="button"
        >
          {region?.value}
        </button>
      ))
    ) : (
      <div className="sdkui:flex sdkui:items-center sdkui:justify-center sdkui:p-4 sdkui:text-dark-60 sdkui:text-sm">
        Ничего не найдено
      </div>
    );
  };

  return (
    <div className="sdkui:pointer-events-auto sdkui:relative sdkui:w-full">
      <Input
        autoComplete="off"
        className={cn(
          "sdkui:h-11 sdkui:rounded-none sdkui:border-0 sdkui:border-dark-40 sdkui:border-b sdkui:bg-transparent sdkui:px-0 sdkui:text-base sdkui:shadow-none sdkui:focus-visible:border-dark-60 sdkui:focus-visible:ring-0",
          value?.value &&
            "sdkui:not-focus:placeholder:text-dark-100 sdkui:placeholder:transition-colors"
        )}
        id={fieldId}
        onBlur={handleFocusBlur}
        onChange={handleInputChange}
        onFocus={handleFocusBlur}
        placeholder={value?.value ?? inputPlaceholder}
        value={inputValue}
      />
      {opened && (
        <div className="sdkui:relative">
          <div
            className="sdkui:absolute sdkui:top-2 sdkui:z-10 sdkui:w-full sdkui:overflow-hidden sdkui:rounded-xl sdkui:border sdkui:border-zinc-100 sdkui:bg-white sdkui:shadow-md"
            ref={regionsListRef}
          >
            <div className="sdkui:max-h-64 sdkui:overflow-y-auto">
              {renderRegionsList()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
